import { EnumSelect } from '@core/components';
import { useSnapshot } from 'valtio';
import { useEffect, useMemo } from 'react';
import { publicLayersState, setSelectedLayer } from '../AlkisMap/layersState';
import type { LayerWithUrl } from '../AlkisMap/useApiImap';

export const PublicLayerSelector = () => {
  const { allLayers, selectedLayer } = useSnapshot(publicLayersState);

  const getDomainFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      const parts = hostname.split('.');
      if (parts.length >= 2) {
        return parts[parts.length - 2];
      }
      return hostname;
    } catch {
      return url;
    }
  };

  const getLayerName = (layer: LayerWithUrl, index: number) => {
    const title = layer.name || layer.layerName || `Layer ${index + 1}`;
    const group = layer.groupName ? `${layer.groupName} | ` : '';
    const domain = getDomainFromUrl(layer.url || '');
    return `${index + 1} | ${group}${title} | ${domain}`;
  };

  // Create options for the dropdown
  const layerOptions = useMemo(
    () =>
      allLayers
        .filter((layer) => Boolean(layer.url && (layer.name || layer.layerName)))
        .map((layer, index) => ({
          value: getLayerName(layer, index),
          layer,
        })),
    [allLayers],
  );

  const selectedOption = useMemo(
    () =>
      layerOptions.find(
        (option) =>
          (option.layer.id && option.layer.id === selectedLayer?.id) ||
          ((option.layer.layerName || option.layer.name) ===
            (selectedLayer?.layerName || selectedLayer?.name) &&
            option.layer.url === selectedLayer?.url),
      ),
    [layerOptions, selectedLayer],
  );

  // Auto-select first available layer if none is selected or selection is out of sync
  useEffect(() => {
    if (layerOptions.length > 0 && !selectedOption) {
      setSelectedLayer(layerOptions[0].layer);
    }
  }, [layerOptions, selectedOption]);

  const currentValue = selectedOption?.value ?? (layerOptions[0]?.value || '');

  const handleLayerChange = (event: { target: { value: string | string[] } }) => {
    const selectedLayerName = event.target.value;
    const layer = layerOptions.find((option) => option.value === selectedLayerName)?.layer;
    setSelectedLayer(layer || null);
  };

  return (
    <EnumSelect
      label="Alkis Layer"
      value={currentValue}
      onChange={handleLayerChange}
      options={layerOptions.map((option) => option.value)}
    />
  );
};
